/* iter460 — AI Analysis Packs section.
 * Shows the 4 prepaid AI-credit packs + the €1.99 single-unlock option as a
 * separate card grid on the pricing page. Region-independent prices come
 * directly from /api/payments/mollie/public-info → ai_packs[].
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";
import { Zap, Sparkles, Rocket, Crown, Package } from "lucide-react";
import MollieCheckoutModal from "@/components/payments/MollieCheckoutModal";

const ICONS = {
  single_unlock:     Sparkles,
  ai_pack_emergency: Zap,
  ai_pack_standard:  Package,
  ai_pack_power:     Rocket,
  ai_pack_wholesale: Crown,
};

const ACCENTS = {
  single_unlock:     "#94A3B8",
  ai_pack_emergency: "#F97316",
  ai_pack_standard:  "#0EA5E9",
  ai_pack_power:     "#8B5CF6",
  ai_pack_wholesale: "#EAB308",
};

export default function AiAnalysisPacks() {
  const { user, API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [packs, setPacks] = useState(null);
  const [modalPlanId, setModalPlanId] = useState(null);
  const [modalLabel, setModalLabel] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/payments/mollie/public-info`)
      .then(({ data }) => { if (!cancelled) setPacks(data?.ai_packs || []); })
      .catch(() => { if (!cancelled) setPacks([]); });
    return () => { cancelled = true; };
  }, [API]);

  if (!packs || packs.length === 0) return null;

  const handleBuy = (pack) => {
    if (!user) {
      toast.error(bg ? "Влезте в профила си за да купите пакет" : "Sign in to buy a pack");
      return;
    }
    setModalPlanId(pack.id);
    setModalLabel(bg ? pack.label_bg : pack.label_en);
  };

  const cardBg = isDark ? "bg-[hsl(223,47%,9%)] border-[hsl(223,47%,16%)]" : "bg-white border-slate-100 shadow-sm";
  const titleColor = isDark ? "text-white" : "text-[#1A1A2E]";
  const subColor = isDark ? "text-slate-400" : "text-slate-500";

  return (
    <section className={`py-14 sm:py-20 ${isDark ? "bg-[hsl(223,47%,6%)]" : "bg-slate-50"}`} data-testid="ai-packs-section">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-10 sm:mb-12">
          <p className={`text-xs uppercase tracking-wider font-semibold mb-2 ${isDark ? "text-[#0EA5E9]" : "text-[#0EA5E9]"}`}>
            {bg ? "Добави AI кредити" : "Top up AI credits"}
          </p>
          <h2 className={`text-2xl sm:text-3xl font-bold mb-3 ${titleColor}`}>
            {bg ? "AI Analysis Packs" : "AI Analysis Packs"}
          </h2>
          <p className={`text-sm max-w-2xl mx-auto ${subColor}`}>
            {bg
              ? "Еднократни покупки — без абонамент. Идеално за нуждаещи се от повече анализи извън месечния си план или потребители без активен план."
              : "One-time purchases — no subscription. Perfect for users needing more analyses beyond their monthly plan or those without an active plan."}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {packs.map((pack) => {
            const Icon = ICONS[pack.id] || Sparkles;
            const accent = ACCENTS[pack.id] || "#0EA5E9";
            const perAnalysis = pack.credits > 1 ? (pack.price_eur / pack.credits).toFixed(2) : null;
            return (
              <div
                key={pack.id}
                className={`relative rounded-2xl border p-5 transition-all hover:shadow-md ${cardBg}`}
                data-testid={`ai-pack-${pack.id}`}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                  style={{ background: `${accent}22`, color: accent }}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className={`text-sm font-bold mb-1 ${titleColor}`}>
                  {bg ? pack.label_bg : pack.label_en}
                </h3>
                <p className={`text-xs mb-3 ${subColor}`}>
                  {pack.credits === 1
                    ? (bg ? "1 AI анализ" : "1 AI analysis")
                    : (bg ? `${pack.credits} AI анализа` : `${pack.credits} AI analyses`)}
                </p>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className={`text-2xl font-bold ${titleColor}`}>€{pack.price_eur}</span>
                </div>
                {perAnalysis && (
                  <p className={`text-[10px] mb-4 ${subColor}`}>
                    {bg ? `€${perAnalysis} на анализ` : `€${perAnalysis} per analysis`}
                  </p>
                )}
                {!perAnalysis && <div className="h-[14px] mb-4" />}
                <button
                  onClick={() => handleBuy(pack)}
                  data-testid={`ai-pack-buy-${pack.id}`}
                  className="w-full text-xs font-semibold rounded-full px-3 py-2 text-white transition-opacity hover:opacity-90"
                  style={{ background: accent }}
                >
                  {bg ? "Купи сега" : "Buy now"}
                </button>
              </div>
            );
          })}
        </div>

        <p className={`text-center text-xs mt-8 ${subColor}`}>
          {bg
            ? "✓ Без абонамент · ✓ Кредитите не изтичат · ✓ Стапват се с твоя месечен план"
            : "✓ No subscription · ✓ Credits don't expire · ✓ Stack with your monthly plan"}
        </p>
      </div>

      {modalPlanId && (
        <MollieCheckoutModal
          plan={{ name: modalLabel || "AI Pack" }}
          billingPeriod="one_time"
          planIdOverride={modalPlanId}
          isDark={isDark}
          onClose={() => { setModalPlanId(null); setModalLabel(null); }}
        />
      )}
    </section>
  );
}
